import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Gift, Users, UserCheck, Zap } from "lucide-react";
import { getReferralStats, applyReferralCode } from "@/lib/api";

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const STEPS = [
  { icon: Gift,      label: "Share your code", desc: "Send your unique referral code to a friend." },
  { icon: Users,     label: "Friend signs up",  desc: "They create a Wishire account using your code." },
  { icon: Zap,       label: "Both earn +5 pts", desc: "You and your friend each get +5 Wishire Score points." },
];

export default function ReferralPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getReferralStats();
        setStats(data);
      } catch {
        setStats({ referral_code: "WISH-DEMO42", total_referred: 3, successful_referrals: 2 });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const code = stats?.referral_code ?? "—";

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = async () => {
    if (!codeInput.trim()) return;
    setApplying(true);
    setApplyMsg(null);
    try {
      await applyReferralCode(codeInput.trim());
      setApplyMsg({ ok: true, text: "Code applied! +5 pts added to your score." });
      setCodeInput("");
    } catch (e) {
      setApplyMsg({ ok: false, text: e?.response?.data?.detail || "Invalid or already used code." });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="container-x py-10 md:py-14 space-y-10">
      {/* Header */}
      <motion.div variants={fadeUp} initial="hidden" animate="show">
        <span className="overline">Growth Loop</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Earn score points</h1>
        <p className="text-stone-500 mt-2 max-w-lg leading-relaxed">
          Refer friends to Wishire. When they sign up, you both earn +5 Wishire Score points.
        </p>
      </motion.div>

      {/* Referral code card */}
      <motion.div
        variants={fadeUp} initial="hidden" animate="show"
        transition={{ delay: 0.1 }}
        className="rounded-[2rem] bg-stone-900 text-stone-50 p-10 md:p-14 relative overflow-hidden wsh-grain"
      >
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(500px 300px at 80% 10%, rgba(217,119,6,0.4), transparent 60%)" }}
        />
        <div className="relative">
          <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Your referral code</span>
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 px-6 py-4 rounded-2xl bg-white/10 border border-white/20">
              <span className="font-mono text-3xl md:text-4xl font-bold tracking-[0.15em] text-amber-300">
                {loading ? "———————" : code}
              </span>
            </div>
            <button
              onClick={copyCode}
              className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl bg-amber-300 text-stone-900 font-medium hover:bg-amber-200 transition-colors"
            >
              {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy code</>}
            </button>
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-xs">
            <StatPill icon={Users} label="Total referred" value={stats?.total_referred ?? 0} />
            <StatPill icon={UserCheck} label="Successful" value={stats?.successful_referrals ?? 0} />
          </div>
        </div>
      </motion.div>

      {/* How it works */}
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
        <span className="overline">How it works</span>
        <h2 className="font-display text-2xl mt-2 mb-6">Three simple steps.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STEPS.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-stone-200 bg-white p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-mono font-bold text-sm">
                  {i + 1}
                </div>
                <div className="p-2 rounded-xl bg-stone-100 text-stone-600">
                  <step.icon size={16} />
                </div>
              </div>
              <h3 className="font-display text-lg mb-1">{step.label}</h3>
              <p className="text-sm text-stone-500 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Apply a code */}
      <motion.div
        variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
        className="rounded-3xl border border-stone-200 bg-[#F5F3ED] p-8 md:p-10"
      >
        <span className="overline">Have a referral code?</span>
        <h2 className="font-display text-2xl mt-2 mb-4">Enter it here to claim your points.</h2>
        <div className="flex flex-col sm:flex-row gap-3 max-w-md">
          <input
            type="text"
            placeholder="e.g. WISH-ABC123"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            className="flex-1 px-5 py-3 rounded-full bg-white border border-stone-300 font-mono text-sm uppercase tracking-widest focus:outline-none focus:border-stone-900"
          />
          <button
            onClick={handleApply}
            disabled={!codeInput.trim() || applying}
            className="btn-primary disabled:opacity-50"
          >
            {applying ? "Applying…" : "Apply code"}
          </button>
        </div>
        {applyMsg && (
          <p className={`mt-3 text-sm font-medium ${applyMsg.ok ? "text-emerald-600" : "text-red-500"}`}>
            {applyMsg.text}
          </p>
        )}
      </motion.div>
    </div>
  );
}

function StatPill({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white/10 border border-white/15 p-4">
      <Icon size={14} className="text-amber-300 mb-1" />
      <p className="font-display text-2xl text-white">{value}</p>
      <p className="text-xs text-stone-400 mt-0.5">{label}</p>
    </div>
  );
}
